import { useCallback, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Card, Checkbox, Spin, Tooltip, Typography } from 'antd';

import type User from 'classes/User';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE, MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL } from 'helpers/constants';
import { tap } from 'rxjs/operators';
import { ANTICIPATION_HELP_URL } from 'settings';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import ConfirmTransactionMessage from 'v2/components/Shared/ConfirmTransactionMessage';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Tour from 'v2/components/Shared/Tour';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { useGetFeatureAccess } from 'v2/hooks/useGetFeatureAccess';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import { EFromPageTransfer } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import { Icon } from '@eduzz/ui-antd-theme';

import { steps } from './tour';

interface IProps {
  user: User;
}

const CardTransferPJ = ({ user }: IProps) => {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);
  const [transferChecked, setTransferChecked] = useState(false);
  const [anticipationChecked, setAnticipationChecked] = useState(false);
  const [userState, setUserState] = useState({ ...user });
  const permissions = useGetPermissions();
  const { data: registryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const minToTransfer = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;

  const [listAnticipation, error] = useObservable(() => financialService.listAnticipation().pipe(), []);
  const [listWallet, walletError] = useObservable(() => financialService.listAvailable(user?.type).pipe(), []);
  const [bankAccounts, bankAccountsError] = useObservable(
    () => financialService.listBankAccounts({ active: 'true' }).pipe(),
    []
  );

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const { data: featureAccessData } = useGetFeatureAccess();

  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
  const transferAccessBlocked = featureAccessData?.transfer.status === 'blocked';
  const anticipationAccessBlocked = featureAccessData?.anticipation.status === 'blocked';
  const shouldBlockTransferBtn = userHasBlockTransferBetaModule || transferAccessBlocked;

  const handleCheckTransfer = useCallback(() => setTransferChecked(!transferChecked), [transferChecked]);
  const handleCheckAnticipation = useCallback(
    () => setAnticipationChecked(!anticipationChecked),
    [anticipationChecked]
  );

  const cannotAntecipate = eduzzAccountsService.verifyUserHasBetaModule('cannot-antecipate');

  const totalToTransfer = useMemo(() => {
    const anticipationValue = anticipationChecked ? listWallet?.available_antecipation || 0 : 0;
    const transferValue = transferChecked ? listWallet?.value || 0 : 0;

    return anticipationValue + transferValue;
  }, [anticipationChecked, listWallet, transferChecked]);

  const disableTransferButton = useMemo(() => {
    if (!transferChecked && anticipationChecked) {
      return true;
    }

    if (totalToTransfer < minToTransfer) {
      return true;
    }

    if (shouldBlockTransferBtn) {
      return true;
    }

    return false;
  }, [anticipationChecked, minToTransfer, totalToTransfer, transferChecked, shouldBlockTransferBtn]);

  const code = useMemo(() => (userState?.crossborder ? 'USD' : 'BRL'), [userState]);
  const invoices = useMemo(
    () => (anticipationChecked && listAnticipation?.invoices) || [],
    [anticipationChecked, listAnticipation]
  );

  const commissionsToAnticipate = invoices.flatMap(invoice => invoice.commissions.map(commision => commision.id));

  const bankSelected = useMemo(() => {
    if (bankAccounts?.length) {
      const findAccount = bankAccounts.find(item => item.main);

      if (!findAccount) {
        return bankAccounts[0];
      }

      return findAccount;
    }

    return null;
  }, [bankAccounts]);

  const isLoading = listAnticipation === null && error === undefined;
  const isLoadingBankAccounts = bankAccounts === null && bankAccountsError === undefined;
  const isLoadingListWallet = listWallet === null && walletError === undefined;

  const [sendTransfer] = useCallbackObservable(() => {
    setLoading(true);
    return financialService
      .sendTransfer({
        commissions: commissionsToAnticipate,
        transfer_method_id: bankSelected.id,
        value: totalToTransfer,
        from: EFromPageTransfer.NEW_CARD
      })
      .pipe(
        tap(
          () => {
            message.success('Sucesso, transferência enviada para processamento com êxito!');
            setLoading(false);
            setUserState({ ...user, has_transfer: true });
          },
          err => {
            setLoading(false);
            message.error(err instanceof Error ? err.message : 'Não foi possível processar a transferência');
          }
        )
      );
  }, [totalToTransfer, bankSelected, invoices]);

  const makeTransfer = useCallback(() => {
    modal.confirm({
      content: <ConfirmTransactionMessage value={totalToTransfer} bankAccount={bankSelected} />,
      title: 'Atenção',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      cancelButtonProps: { type: 'text' },
      onOk: sendTransfer
    });
  }, [bankSelected, modal, sendTransfer, totalToTransfer]);

  const handleSubmit = useCallback(() => {
    if (!transferChecked && !anticipationChecked) {
      message.error('Selecione os valores para transferência');
      return;
    }

    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    makeTransfer();
  }, [anticipationChecked, makeTransfer, message, shouldBlock, transferChecked]);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    makeTransfer();
  }, [makeTransfer, dismissBlock]);

  const renderTooltipValue = useMemo(() => {
    if (userHasBlockTransferBetaModule) {
      return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    }

    if (featureAccessData?.transfer.status === 'blocked') {
      return featureAccessData?.transfer.reason;
    }

    if (!transferChecked && anticipationChecked) {
      return 'Para antecipar é necessário selecionar o saldo a transferir';
    }

    if (totalToTransfer < minToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={minToTransfer} format={code}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [
    featureAccessData,
    anticipationChecked,
    code,
    minToTransfer,
    totalToTransfer,
    transferChecked,
    userHasBlockTransferBetaModule
  ]);

  const disableCheckbox = useMemo(() => {
    return (
      isLoadingBankAccounts || !bankSelected || userState?.has_transfer || bankAccountsError || shouldBlockTransferBtn
    );
  }, [bankAccountsError, bankSelected, isLoadingBankAccounts, userState, shouldBlockTransferBtn]);

  const renderTransferButton = useMemo(() => {
    if (isLoadingBankAccounts) {
      return null;
    }

    if (!bankSelected) {
      return <Typography.Text>Você precisa de uma conta bancária cadastrada para transferir</Typography.Text>;
    }

    if (userState?.has_transfer) {
      return <Typography.Text>Você tem transferências em processamento</Typography.Text>;
    }

    if (bankAccountsError) {
      return <Typography.Text type='danger'>Erro ao buscar contas bancárias</Typography.Text>;
    }

    return (
      <Tooltip title={renderTooltipValue} placement='top'>
        <div>
          <Button
            color='green'
            variant='solid'
            disabled={loading || disableTransferButton}
            onClick={handleSubmit}
            id='myTransferButton'
            icon={<Icon.ArrowRightLeft size={14} />}
          >
            Transferir agora
          </Button>
        </div>
      </Tooltip>
    );
  }, [
    bankAccountsError,
    bankSelected,
    disableTransferButton,
    handleSubmit,
    isLoadingBankAccounts,
    loading,
    renderTooltipValue,
    userState?.has_transfer
  ]);

  const transferBlockedTooltipMessage = () => {
    if (transferAccessBlocked) {
      return featureAccessData?.transfer.reason;
    }

    return '';
  };

  const anticipationBlockedTooltipMessage = () => {
    if (anticipationAccessBlocked) {
      return featureAccessData?.anticipation.reason;
    }

    return '';
  };

  return (
    <>
      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />

      <Tour name='card_transfer_pj_tour' steps={steps} />

      <div id='transfer-card-pj' className='h-full'>
        <Card className='flex h-full flex-col' classNames={{ body: 'flex flex-1 flex-col' }} title={`Wallet (${code})`}>
          <div className='flex flex-1 flex-col'>
            <div id='transfer-card-pj-values' className='flex flex-col items-end gap-4'>
              <div className='flex flex-col items-end'>
                <Typography.Text className='text-base'>Saldo disponível para transferência:</Typography.Text>
                <Typography.Text className='text-sm' type='secondary'>
                  Total de ganhos disponíveis para transferir
                </Typography.Text>
                <div className='flex items-center justify-end'>
                  <Typography.Text className='text-lg' strong>
                    {!isLoadingListWallet ? (
                      <Mask>
                        <FormattedNumber format={code} value={listWallet?.value || 0} />
                      </Mask>
                    ) : (
                      <Spin size='small' />
                    )}
                  </Typography.Text>

                  {!isLoading && (
                    <Tooltip className='ml-2' title={transferBlockedTooltipMessage()} placement='top'>
                      <div>
                        <Checkbox
                          disabled={disableCheckbox}
                          checked={transferChecked}
                          onChange={handleCheckTransfer}
                          aria-label='Selecionar saldo disponível para transferência'
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>

              {!cannotAntecipate && (
                <div className='flex flex-col items-end'>
                  <Typography.Text className='text-base'>Saldo disponível para antecipar:</Typography.Text>
                  <Typography.Text className='text-sm' type='secondary'>
                    Ganhos que podem ser antecipados
                  </Typography.Text>
                  <div className='flex items-center justify-end'>
                    <Typography.Text className='text-lg' strong>
                      {!isLoadingListWallet ? (
                        <Mask>
                          <FormattedNumber format={code} value={listWallet?.available_antecipation || 0} />
                        </Mask>
                      ) : (
                        <Spin size='small' />
                      )}
                    </Typography.Text>

                    {!isLoading && (
                      <Tooltip className='ml-2' title={anticipationBlockedTooltipMessage()} placement='top'>
                        <div>
                          <Checkbox
                            disabled={disableCheckbox}
                            checked={anticipationChecked}
                            onChange={handleCheckAnticipation}
                            aria-label='Selecionar saldo disponível para antecipar'
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className='mt-auto flex items-center justify-between'>
              {!cannotAntecipate && (
                <Button
                  href={ANTICIPATION_HELP_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  color='default'
                  variant='filled'
                  shape='circle'
                  icon={<Icon.CircleHelp size={16} />}
                  classNames={{ icon: 'flex' }}
                  aria-label='Entenda a antecipação'
                />
              )}
              <PermissionHide action='myeduzz:dashboard:solicitar-transferencia'>{renderTransferButton}</PermissionHide>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

export default CardTransferPJ;
